import React, { useEffect, useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'

import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { Fieldset, FieldRow } from '../../../../../generic/Form'
import { updateNormalization } from '../../../../../services'
import { INormalization, INormalizationThreshold } from '../../../../../types'
import { NormalizationStyled } from './styles'

interface IProps {
    normalization?: INormalization
    minimum?: number
}

export const Normalization: React.FC<IProps> = ({ normalization }) => {
    const [editing, setEditing] = useState(false)
    const [normalizations, setNormalizations] = useState<INormalization>()
    const [copyNormalizations, setCopyNormalizations] = useState<INormalization>()
    const { handleSubmit } = useForm<FieldValues>()

    useEffect(() => {
        if (normalization) {
            normalization.thresholds = normalization?.thresholds?.reverse() ?? []
            setNormalizations(normalization)
        }
    }, [normalization])

    const handleCancelButton = (): void => {
        setEditing(false)
        setNormalizations({ ...copyNormalizations })
    }

    const handleEditButton = (): void => {
        setEditing(true)
        const copy = JSON.parse(JSON.stringify(normalization))
        setCopyNormalizations({ ...copy })
    }

    const handleRefreshConfig = (): void => {
        setNormalizations({ ...normalizations })
    }

    const onSaveCategory = async (): Promise<void> => {
        await updateNormalization(normalizations)
            .then(data => {
                setNormalizations({ ...data })
                globalNotification.success(i18n('Normalization updated successfully.'))
            })
            .catch(reject => {
                setNormalizations({ ...copyNormalizations })
                globalNotification.error(reject?.Message ?? reject?.message ?? reject?.error ?? i18n(reject))
            })

        setEditing(false)
    }

    const handleAddNormalizationRule = (): void => {
        const total = normalizations.thresholds.length
        const threshold: INormalizationThreshold = normalizations.thresholds.at(total - 1)
        const newThreshold: INormalizationThreshold = { ...threshold }

        if (newThreshold.threshold > 100) {
            return
        }

        normalizations.thresholds.splice(total - 1, 0, newThreshold)
        setNormalizations({ ...normalizations })
    }

    const deleteRule = (index: number): void => {
        normalizations?.thresholds?.splice(index, 1)
        setNormalizations({ ...normalizations })
    }

    const generateNormalizationDefaultObject = (): void => {
        setNormalizations({
            ...normalizations,
            thresholds: [
                { threshold: 0, pfd: 1 },
                { threshold: 50, pfd: 0.001 }
            ]
        })
    }

    return (
        <NormalizationStyled>
            <Fieldset
                title={i18n('NORMALIZATION TITLE')}
                editing={editing}
                onCancel={handleCancelButton}
                toggleEdit={handleEditButton}
                onSubmit={handleSubmit(onSaveCategory)}
                withFieldset={true}
            >
                <FieldRow>
                    <div style={{ padding: '15px' }}>{i18n('NORMALIZATION METHOD')}</div>
                </FieldRow>

                <FieldRow></FieldRow>
                <div className="normalization-list">
                    <div className="normalization-list-header">
                        <div className="header-item min">QLR % min</div>
                        <div className="header-item max">QLR % max</div>
                        <div className="header-item equals"></div>
                        <div className="header-item pfd">PFD</div>
                    </div>
                    <div className="normalization-content">
                        {normalizations &&
                            normalizations?.thresholds?.map((ths, index, vet) => {
                                return (
                                    <>
                                        {editing && index === vet.length - 1 && (
                                            <div className="threshold-settings-input-ruleblock">
                                                <div className="threshold-settings-input-newruleline" />
                                                <div
                                                    className="threshold-settings-input-button"
                                                    onClick={() => {
                                                        handleAddNormalizationRule()
                                                    }}
                                                >
                                                    + {i18n('New Rule')}
                                                </div>
                                                <div className="threshold-settings-input-newruleline" />
                                            </div>
                                        )}
                                        <FieldRow className={`box-threshold ${editing ? 'editing' : ''}`}>
                                            <input
                                                disabled
                                                readOnly
                                                value={ths.threshold}
                                                type={'number'}
                                                className="box-threshold-item min"
                                            />
                                            <input
                                                disabled={index === vet.length - 1}
                                                onChange={(e: any) => {
                                                    vet[index + 1].threshold = Number(e.target.value)
                                                    handleRefreshConfig()
                                                }}
                                                min={ths.threshold}
                                                max={vet?.at(index + 2)?.threshold ?? 100}
                                                step={1}
                                                type={'number'}
                                                value={vet?.at(index + 1)?.threshold ?? 100}
                                                className="box-threshold-item max"
                                            />
                                            <div className="box-threshold-item equals"> = </div>
                                            <input
                                                className="box-threshold-item pfd"
                                                step={0.1}
                                                type="number"
                                                onChange={(e: any) => {
                                                    ths.pfd = Number(e.target.value)
                                                    handleRefreshConfig()
                                                }}
                                                value={ths.pfd}
                                            />
                                            {editing && index !== 0 && index !== vet.length - 1 && (
                                                <div
                                                    className="threshold-settings-input-delete-button"
                                                    onClick={() => {
                                                        deleteRule(index)
                                                    }}
                                                >
                                                    <i className="far fa-trash-alt" />
                                                </div>
                                            )}
                                        </FieldRow>
                                    </>
                                )
                            })}
                    </div>
                </div>
            </Fieldset>
        </NormalizationStyled>
    )
}
